// src/pages/admin/tabs/BookingsTab.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Admin list of WhatsApp video-call appointments. Readable by any active admin;
// status/reschedule/reassign actions are restricted to super admins (the
// backend also enforces this). Talks to products-app /api/video-call/bookings*.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect } from "react";
import { useAuth } from "../../../hooks/useAuth";
import { B, fonts, Spinner, Btn } from "../../../components/UI";

const PRODUCTS_API_URL =
  process.env.REACT_APP_PRODUCTS_API_URL || "https://products.nilamsynthetics.com";
const API = (p) => `${PRODUCTS_API_URL}/api/video-call${p}`;

const STATUS_STYLE = {
  confirmed: { bg: B.greenLight, color: B.green, label: "Confirmed" },
  completed: { bg: B.orangeLight || "#f9eee8", color: B.textMid, label: "Completed" },
  cancelled: { bg: B.redLight, color: B.red, label: "Cancelled" },
  no_show: { bg: B.redLight, color: B.red, label: "No-show" },
};

const inputStyle = {
  width: "100%", padding: "8px 10px", fontSize: 13, fontWeight: 500,
  fontFamily: fonts.body, border: `1.5px solid ${B.border}`,
  borderRadius: 8, outline: "none", background: B.white, color: B.text,
  boxSizing: "border-box",
};

const card = () => ({
  background: B.white, border: `1px solid ${B.borderLight}`,
  borderRadius: 14, padding: "16px 20px",
});

const fmtDate = (d) => d || "—";
const fmtPhone = (a) => (a.whatsapp_phone || a.mobile_national || "—").replace(/^(91)/, "+91 ");

const dayOffsetISO = (offsetDays) => {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export default function BookingsTab() {
  const { user, isSuperAdmin } = useAuth();

  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);

  // Filters (default: From today, To tomorrow, Status confirmed).
  const [from, setFrom] = useState(dayOffsetISO(0));
  const [to, setTo] = useState(dayOffsetISO(1));
  const [status, setStatus] = useState("confirmed");
  const [phone, setPhone] = useState("");

  // Active representatives available for assignment.
  const [reps, setReps] = useState([]);

  const flash = (msg) => { setNotice(msg); setTimeout(() => setNotice(""), 3500); };

  const getToken = async () => {
    if (!user) throw new Error("Not signed in.");
    return user.getIdToken();
  };

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const token = await getToken();
      const q = new URLSearchParams();
      if (from) q.set("from", from);
      if (to) q.set("to", to);
      if (status) q.set("status", status);
      if (phone.trim()) q.set("phone", phone.trim());
      const res = await fetch(`${API("/bookings")}?${q.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || "Failed to load bookings.");
      setBookings(json.data || []);
    } catch (e) {
      setError(e.message || "Failed to load bookings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, []);

  // Load the list of active representatives for the assign / test-send UI.
  const loadReps = async () => {
    try {
      const token = await getToken();
      const res = await fetch(API("/reps"), { headers: { Authorization: `Bearer ${token}` } });
      const json = await res.json().catch(() => ({}));
      if (res.ok) setReps(json.data || []);
    } catch { /* non-fatal — assignment can still use the prompt fallback */ }
  };
  useEffect(() => { loadReps(); /* eslint-disable-line */ }, []);

  const doAction = async (id, body, successMsg) => {
    if (!isSuperAdmin) return;
    setBusyId(id);
    setError("");
    try {
      const token = await getToken();
      const res = await fetch(API(`/bookings/${id}`), {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(json.error || (json.errors && json.errors.join("; ")) || "Action failed.");
        return;
      }
      flash(successMsg);
      await load();
    } catch (e) {
      setError(e.message || "Action failed.");
    } finally {
      setBusyId(null);
    }
  };

  // Lightweight guard: browser prompt is optional; actions are never blocked on it.
  const ask = (msg, def) => {
    try {
      return typeof window !== "undefined" && typeof window.prompt === "function"
        ? window.prompt(msg, def) : "";
    } catch {
      return "";
    }
  };

  const handleCancel = async (a) => {
    const reason = (ask("Reason for cancellation (optional):", "") || "").trim();
    await doAction(a.id, { status: "cancelled", ...(reason ? { cancellation_reason: reason } : {}) }, "Booking cancelled.");
  };

  const handleReassign = async (a, repId) => {
    if (!isSuperAdmin) return;
    const rep = reps.find(r => r.id === repId);
    const body = rep
      ? { assigned_rep_uid: rep.id, assigned_rep_name: rep.name, assigned_rep_phone: rep.whatsapp_phone }
      : { assigned_rep_uid: null, assigned_rep_name: null, assigned_rep_phone: null };
    await doAction(a.id, body, rep ? `Assigned to ${rep.name}` : "Rep unassigned.");
  };

  // "Template sender": push the reminder template to a rep's WhatsApp on demand
  // (e.g. to test the Meta template or nudge a rep without waiting for the cron).
  const handleSendReminder = async (a) => {
    if (!isSuperAdmin) return;
    setBusyId(a.id);
    setError("");
    try {
      const token = await getToken();
      const res = await fetch(`${PRODUCTS_API_URL}/api/notifications/rep-reminders/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          rep_id: a.assigned_rep_uid || undefined,
          whatsapp_phone: a.assigned_rep_phone || "",
          name: a.assigned_rep_name || "",
          customer_name: a.customer_name,
          appointment_date: a.appointment_date,
          start_time: a.start_time,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) { setError(json.error || "Send failed."); return; }
      flash(`Reminder sent to ${a.assigned_rep_name || a.assigned_rep_phone}.`);
    } catch (e) {
      setError(e.message || "Send failed.");
    } finally {
      setBusyId(null);
    }
  };

  const CONTINUE = true;

  if (loading && bookings.length === 0) {
    return (
      <div style={{ padding: "30px 24px", fontFamily: fonts.body, color: B.textMid }}>
        <Spinner /> <span style={{ marginLeft: 8 }}>Loading bookings…</span>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20, maxWidth: 980 }}>

      {notice && (
        <div style={{ background: B.greenLight, color: B.green, border: `1px solid ${B.green}`, borderRadius: 10, padding: "10px 14px", fontSize: 13, fontFamily: fonts.body }}>
          ✓ {notice}
        </div>
      )}
      {error && (
        <div style={{ background: B.redLight, color: B.red, border: `1px solid ${B.red}`, borderRadius: 10, padding: "10px 14px", fontSize: 13, fontFamily: fonts.body }}>
          {error}
        </div>
      )}

      <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: B.text, fontFamily: fonts.adminDisplay }}>Bookings</h3>

      {/* ─── Filters ─── */}
      <div style={card()}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr auto", gap: 12, alignItems: "end" }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: B.textMid, marginBottom: 4, fontFamily: fonts.body }}>From</div>
            <input style={{ ...inputStyle, width: "100%" }} type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: B.textMid, marginBottom: 4, fontFamily: fonts.body }}>To</div>
            <input style={{ ...inputStyle, width: "100%" }} type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: B.textMid, marginBottom: 4, fontFamily: fonts.body }}>Status</div>
            <select style={{ ...inputStyle, width: "100%" }} value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All</option>
              <option value="confirmed">Confirmed</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="no_show">No-show</option>
            </select>
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: B.textMid, marginBottom: 4, fontFamily: fonts.body }}>Phone</div>
            <input style={{ ...inputStyle, width: "100%" }} value={phone} placeholder="Search" onChange={(e) => setPhone(e.target.value)} />
          </div>
          <Btn variant="primary" small onClick={load}>Apply</Btn>
        </div>
      </div>

      {loading && <div style={{ fontFamily: fonts.body, color: B.textMid, fontSize: 13 }}><Spinner /> Refreshing…</div>}

      {bookings.length === 0 && !loading && (
        <p style={{ color: B.textLight, fontFamily: fonts.body, fontSize: 13 }}>No bookings match the current filters.</p>
      )}

      {bookings.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ color: B.textMid, fontFamily: fonts.body, fontSize: 12 }}>
                <th style={{ textAlign: "left", padding: "8px 10px" }}>#</th>
                <th style={{ textAlign: "left", padding: "8px 10px" }}>Date &amp; Time</th>
                <th style={{ textAlign: "left", padding: "8px 10px" }}>Customer</th>
                <th style={{ textAlign: "left", padding: "8px 10px" }}>Phone</th>
                <th style={{ textAlign: "left", padding: "8px 10px" }}>Source</th>
                <th style={{ textAlign: "left", padding: "8px 10px" }}>Rep</th>
                <th style={{ textAlign: "left", padding: "8px 10px" }}>Status</th>
                <th style={{ textAlign: "right", padding: "8px 10px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((a) => {
                const st = STATUS_STYLE[a.status] || STATUS_STYLE.confirmed;
                return (
                  <tr key={a.id} style={{ borderBottom: `1px solid ${B.borderLight}`, fontFamily: fonts.body, fontSize: 13 }}>
                    <td style={{ padding: "10px 10px", fontWeight: 600, color: B.text }}>{a.appointment_number || a.id.slice(0, 6)}</td>
                    <td style={{ padding: "10px 10px", color: B.text }}>{fmtDate(a.appointment_date)}<br /><span style={{ color: B.textLight, fontSize: 12 }}>{a.start_time}–{a.end_time}</span></td>
                    <td style={{ padding: "10px 10px", color: B.text }}>{a.customer_name}{a.business_name ? <span style={{ color: B.textLight, fontSize: 12 }}><br />{a.business_name}</span> : null}</td>
                    <td style={{ padding: "10px 10px", color: B.textMid, whiteSpace: "nowrap" }}>{fmtPhone(a)}</td>
                    <td style={{ padding: "10px 10px", color: B.textMid }}>{a.source || "—"}</td>
                    <td style={{ padding: "10px 10px", color: B.textMid }}>
                      {isSuperAdmin && a.status === "confirmed" ? (
                        <select
                          disabled={busyId === a.id}
                          value={a.assigned_rep_uid || ""}
                          onChange={(e) => handleReassign(a, e.target.value)}
                          title="Assign representative"
                          style={{ maxWidth: 140, padding: "5px 8px", fontSize: 12, borderRadius: 6, border: `1.5px solid ${B.border}`, background: B.white, color: B.text, fontFamily: fonts.body, outline: "none", cursor: "pointer" }}
                        >
                          {a.assigned_rep_name && !reps.some((r) => r.id === a.assigned_rep_uid)
                            ? <option value="">{a.assigned_rep_name}</option>
                            : <option value="">Unassigned</option>}
                          {reps.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                        </select>
                      ) : (
                        <span style={{ color: B.textMid }}>{a.assigned_rep_name || "—"}</span>
                      )}
                    </td>
                    <td style={{ padding: "10px 10px" }}>
                      <span style={{ background: st.bg, color: st.color, borderRadius: 4, padding: "2px 8px", fontWeight: 700, fontSize: 11, fontFamily: fonts.body }}>{st.label}</span>
                    </td>
                    <td style={{ padding: "10px 10px", textAlign: "right", whiteSpace: "nowrap" }}>
                      {isSuperAdmin && a.status === "confirmed"
                        ? (
                          <span style={{ display: "inline-flex", gap: 5, alignItems: "center" }}>
                            <button type="button" disabled={busyId === a.id} title="Mark completed" onClick={() => doAction(a.id, { status: "completed" }, "Marked completed.")} style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: 15 }}>✓</button>
                            <button type="button" disabled={busyId === a.id} title="No-show" onClick={() => doAction(a.id, { status: "no_show" }, "Marked no-show.")} style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: 15 }}>✕</button>
                            <button type="button" disabled={busyId === a.id} title="Cancel" onClick={() => handleCancel(a)} style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: 15 }}>⛔</button>
                            {a.assigned_rep_phone
                              ? <button type="button" disabled={busyId === a.id} title="Send reminder now" onClick={() => handleSendReminder(a)} style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: 15 }}>🔔</button>
                              : null}
                          </span>
                        )
                        : (
                          <span style={{ color: B.textLight, fontSize: 11 }}>{isSuperAdmin ? "—" : "view"}</span>
                        )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}